import { array, get, fn } from '@ember/helper';
import Button from 'tio-reimagined/components/button';
import { Form, Input, Select, type FormResultData } from 'tio-ui/components/forms';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedObject } from '@ember/reactive/collections';
import { VStack, Section } from 'tio-ui/components/layout';
import * as v from 'valibot';
import Component from '@glimmer/component';
import errorsForValibotField from 'tio-common/helpers/errors-for-valibot-field';
import type EmployeeModel from 'tio-common/models/employee';
import type PlanModel from 'tio-common/models/plan';
import type RouterService from '@ember/routing/router';
import type StoreService from 'ember-data/store';

export interface EmployeesCreateEmployeeSignature {
  Args: {
    model: EmployeeModel;
  };
}

// Validation schema for employee creation form
const phoneRegExp =
  /^((\+[1-9]{1,4}[ -]*)|(\([0-9]{2,3}\)[ -]*)|([0-9]{2,4})[ -]*)*?[0-9]{3,4}?[ -]*[0-9]{3,4}?$/;

const employeeFormSchema = v.object({
  firstName: v.pipe(v.string('First name is required'), v.nonEmpty('First name is required')),
  lastName: v.pipe(v.string('Last name is required'), v.nonEmpty('Last name is required')),
  email: v.pipe(
    v.string('E-mail is required'),
    v.nonEmpty('E-mail is required'),
    v.email('Please enter a valid email address')
  ),
  payrollId: v.pipe(v.string('Payroll ID is required'), v.nonEmpty('Payroll ID is required')),
  phoneNumber: v.optional(
    v.union([
      v.pipe(v.string(), v.length(0)),
      v.pipe(v.string(), v.regex(phoneRegExp, 'Please enter a valid phone number')),
    ])
  ),
  employmentType: v.optional(v.string()),
  birthYear: v.optional(v.string()),
  addressLine1: v.optional(v.string()),
  addressLine2: v.optional(v.string()),
  city: v.optional(v.string()),
  state: v.optional(v.string()),
  postalCode: v.optional(v.string()),
  employmentStartDate: v.optional(v.string()),
  employmentEndDate: v.optional(v.string()),
  salary: v.optional(v.string()),
  ratePeriod: v.optional(v.string()),
  title: v.optional(v.string()),
  department: v.optional(v.string()),
  costCenter: v.optional(v.string()),
  eligibilityStartDate: v.optional(v.string()),
  eligibilityEndDate: v.optional(v.string()),
});

export default class EmployeesCreateEmployeeComponent extends Component<EmployeesCreateEmployeeSignature> {
  @service declare store: StoreService;
  @service declare router: RouterService;

  @tracked plan?: PlanModel;
  @tracked hasSubmitted = false;
  @tracked isSubmitting = false;

  // Cache for tracked customAttributes - initialized lazily
  #customAttributes?: Record<string, unknown>;

  get customAttributes(): Record<string, unknown> {
    if (!this.#customAttributes) {
      this.#customAttributes = trackedObject(
        (this.args.model.customAttributes as Record<string, unknown>) ?? {}
      );
    }
    return this.#customAttributes;
  }

  // US States list
  states = [
    'Alabama',
    'Alaska',
    'American Samoa',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'District of Columbia',
    'Florida',
    'Georgia',
    'Guam',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Marshall Islands',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Puerto Rico',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virgin Island',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
  ];

  get birthYears(): string[] {
    const currentYear = new Date().getFullYear();
    const years: string[] = [];
    let startYear = 1920;

    while (startYear <= currentYear) {
      years.push(String(startYear++));
    }
    return years.reverse();
  }

  get formValidationErrors(): Record<string, string[]> {
    const result = v.safeParse(employeeFormSchema, this.args.model);

    if (result.success) {
      return {};
    }

    const validationErrors: Record<string, string[]> = {};
    for (const issue of result.issues) {
      const path = issue.path?.[0]?.key as string;
      if (path) {
        if (!validationErrors[path]) {
          validationErrors[path] = [];
        }
        validationErrors[path].push(issue.message);
      }
    }
    return validationErrors;
  }

  get isFormValid() {
    return Object.keys(this.formValidationErrors).length === 0;
  }

  // Update model directly from form changes using setProperties pattern
  formDidChange = (_result: FormResultData<unknown>, event: Event) => {
    const formData = new FormData(event.currentTarget as HTMLFormElement);
    const data: Record<string, unknown> = {};

    for (const [key, value] of formData.entries()) {
      data[key] = value;
    }

    this.args.model.setProperties(data);
  };

  onSelectPlan = (planId: string) => {
    this.plan = this.args.model.company.plans.find((plan) => plan.id === planId);
  };

  onSelectState = (state: string) => {
    this.args.model.state = state;
  };

  onSelectEmploymentType = (employmentType: string) => {
    this.args.model.employmentType = employmentType;
  };

  onSelectBirthYear = (birthYear: string) => {
    this.args.model.birthYear = birthYear;
  };

  onSelectRatePeriod = (ratePeriod: string) => {
    this.args.model.ratePeriod = ratePeriod;
  };

  saveCustomAttr = (key: string, value: string) => {
    this.customAttributes[key] = value;
  };

  onFormSubmit = async (_result: FormResultData<unknown>, event: SubmitEvent) => {
    event.preventDefault();
    this.hasSubmitted = true;

    if (!this.isFormValid) {
      // Scroll to and focus the first invalid input
      setTimeout(() => {
        const firstErrorInput = document.querySelector(
          '[aria-invalid="true"], [data-has-errors="true"]'
        ) as HTMLElement | null;
        if (firstErrorInput) {
          firstErrorInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
          firstErrorInput.focus();
        }
      }, 100);
      return;
    }

    this.isSubmitting = true;

    try {
      // Copy tracked customAttributes back to the model before saving
      this.args.model.customAttributes = { ...this.customAttributes };
      const newEmployee = await this.args.model.save();

      // Patch plan association if a plan is selected
      if (this.plan) {
        this.plan.employees.push(newEmployee);
        await this.plan.save({ adapterOptions: { createHasMany: [newEmployee] } });
      }

      this.router.transitionTo('authenticated.admin.employees.show', newEmployee.id);
    } catch (error) {
      // Error notification is already shown by the adapter's ensureResponseAuthorized method
      console.error('Failed to create employee:', error);

      // Rollback the model if save failed to reset the form state
      this.args.model.rollbackAttributes();
    } finally {
      this.isSubmitting = false;
    }
  };

  cancel = () => {
    this.router.transitionTo('authenticated.admin.employees.index');
  };

  <template>
    <Form @onChange={{this.formDidChange}} @onSubmit={{this.onFormSubmit}}>
      <VStack>
        {{! Basic Information Section }}
        <Section>
          <:header>{{t "partner.employee.basic_info" default="Basic Information"}}</:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                @label={{t "partner.employee.first_name"}}
                @value={{@model.firstName}}
                @errors={{errorsForValibotField
                  "firstName"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                @isRequired={{true}}
                name="firstName"
              />
              <Input
                @label={{t "partner.employee.last_name"}}
                @value={{@model.lastName}}
                @errors={{errorsForValibotField
                  "lastName"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                @isRequired={{true}}
                name="lastName"
              />
              <Input
                @label={{t "partner.employee.payroll_id"}}
                @value={{@model.payrollId}}
                @errors={{errorsForValibotField
                  "payrollId"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                @isRequired={{true}}
                name="payrollId"
              />
              <Select
                @label={{t "partner.employee.employment_type"}}
                @items={{array "FULL_TIME" "PART_TIME" "EXEMPT" "NON_EXEMPT"}}
                @selectedKey={{@model.employmentType}}
                @onAction={{this.onSelectEmploymentType}}
                @selectionMode="single"
                @errors={{errorsForValibotField
                  "employmentType"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
              />
              <Select
                @isFilterable={{true}}
                @label={{t "partner.employee.birth_year"}}
                @items={{this.birthYears}}
                @selectedKey={{@model.birthYear}}
                @onAction={{this.onSelectBirthYear}}
                @selectionMode="single"
                @errors={{errorsForValibotField
                  "birthYear"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
              />
              <Input
                @label={{t "partner.employee.phone_number"}}
                @value={{@model.phoneNumber}}
                @errors={{errorsForValibotField
                  "phoneNumber"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                name="phoneNumber"
                type="tel"
              />
              <Input
                @label={{t "partner.employee.email"}}
                @value={{@model.email}}
                @errors={{errorsForValibotField
                  "email"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                @isRequired={{true}}
                @type="email"
                name="email"
              />
              <Select
                @label="Plan"
                @items={{@model.company.plans}}
                @onAction={{this.onSelectPlan}}
                @selectionMode="single"
                @errors={{errorsForValibotField
                  "plan"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
              />
            </div>
          </:body>
        </Section>

        {{! Address Section }}
        <Section>
          <:header>{{t "partner.employee.address" default="Address"}}</:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                @label={{t "partner.employee.address_line1"}}
                @value={{@model.addressLine1}}
                @errors={{errorsForValibotField
                  "addressLine1"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                name="addressLine1"
              />
              <Input
                @label={{t "partner.employee.address_line2"}}
                @value={{@model.addressLine2}}
                @errors={{errorsForValibotField
                  "addressLine2"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                name="addressLine2"
              />
              <Input
                @label={{t "partner.employee.city"}}
                @value={{@model.city}}
                @errors={{errorsForValibotField
                  "city"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                name="city"
              />
              <Select
                data-test-state-select
                @label={{t "partner.employee.state"}}
                @items={{this.states}}
                @selectedKey={{@model.state}}
                @onAction={{this.onSelectState}}
                @isFilterable={{true}}
                @selectionMode="single"
                @errors={{errorsForValibotField
                  "state"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
              />
              <Input
                @label={{t "partner.employee.postal_code"}}
                @value={{@model.postalCode}}
                @errors={{errorsForValibotField
                  "postalCode"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                name="postalCode"
              />
            </div>
          </:body>
        </Section>

        {{! Employment Details Section }}
        <Section>
          <:header>{{t
              "partner.employee.employment_details"
              default="Employment Details"
            }}</:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <Input
                @label={{t "common.employment_start_date"}}
                @value={{@model.employmentStartDate}}
                @errors={{errorsForValibotField
                  "employmentStartDate"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                @type="date"
                name="employmentStartDate"
              />
              <Input
                @label={{t "common.employment_end_date"}}
                @value={{@model.employmentEndDate}}
                @errors={{errorsForValibotField
                  "employmentEndDate"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                @type="date"
                name="employmentEndDate"
              />
              <Input
                @label={{t "partner.employee.salary"}}
                @value={{@model.salary}}
                @errors={{errorsForValibotField
                  "salary"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                name="salary"
              />
              <Select
                @label={{t "partner.employee.rate_period"}}
                @items={{array "HOURLY" "WEEKLY" "BI_WEEKLY" "MONTHLY" "YEARLY"}}
                @selectedKey={{@model.ratePeriod}}
                @onAction={{this.onSelectRatePeriod}}
                @selectionMode="single"
                @errors={{errorsForValibotField
                  "ratePeriod"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
              />
              <Input
                @label={{t "partner.employee.title"}}
                @value={{@model.title}}
                @errors={{errorsForValibotField
                  "title"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                name="title"
              />
              <Input
                @label={{t "partner.employee.department"}}
                @value={{@model.department}}
                @errors={{errorsForValibotField
                  "department"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                name="department"
              />
              <Input
                @label={{t "partner.employee.cost_center"}}
                @value={{@model.costCenter}}
                @errors={{errorsForValibotField
                  "costCenter"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                name="costCenter"
              />
            </div>
          </:body>
        </Section>

        {{! Eligibility Section }}
        <Section>
          <:header>{{t "partner.employee.eligibility" default="Eligibility"}}</:header>
          <:body>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                @label={{t "partner.employee.eligibility_start_date"}}
                @value={{@model.eligibilityStartDate}}
                @errors={{errorsForValibotField
                  "eligibilityStartDate"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                @type="date"
                name="eligibilityStartDate"
              />
              <Input
                @label={{t "partner.employee.eligibility_end_date"}}
                @value={{@model.eligibilityEndDate}}
                @errors={{errorsForValibotField
                  "eligibilityEndDate"
                  schemaErrors=this.formValidationErrors
                  hasSubmitted=this.hasSubmitted
                }}
                @type="date"
                name="eligibilityEndDate"
              />
            </div>
          </:body>
        </Section>

        {{! Custom Attributes Section }}
        {{#if @model.company.companySetting.employeeCustomAttributes.length}}
          <Section>
            <:header>{{t
                "partner.employee.custom_attributes"
                default="Custom Attributes"
              }}</:header>
            <:body>
              <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {{#each @model.company.companySetting.employeeCustomAttributes as |attribute|}}
                  {{#if attribute.custom}}
                    <Input
                      @label={{attribute.field}}
                      @value={{get this.customAttributes attribute.field}}
                      @onChange={{fn this.saveCustomAttr attribute.field}}
                    />
                  {{/if}}
                {{/each}}
              </div>
            </:body>
          </Section>
        {{/if}}

        {{! Form Actions }}
        <div class="flex gap-3 justify-end pt-4">
          <Button @intent="danger" @appearance="outlined" {{on "click" this.cancel}}>
            {{t "partner.employee.cancel"}}
          </Button>
          <Button @intent="primary" type="submit" @isDisabled={{this.isSubmitting}}>
            {{t "partner.employee.create_employee"}}
          </Button>
        </div>
      </VStack>
    </Form>
  </template>
}
